import { Box, Collapse, Fade, IconButton, Paper, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import dayjs from "dayjs"
import type { FC } from "react"
import { useEffect, useMemo, useRef, useState } from "react"

import type { PriceChangeSubmitContext, PriceChangeTablesHandle } from "../components/PriceChangeTables"
import { PriceChangeTables } from "../components/PriceChangeTables"

import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import { PermissionActionType } from "@/constants/permissions"
import { PriceChangeHistorySortField, PriceChangeType, SortingOrder } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { BulkUpdateApplyingDialog } from "@/screens/Pricing/components/BulkUpdateApplyingDialog"
import BulkUpdateOptions from "@/screens/Pricing/components/BulkUpdateOptions"
import {
  AddCostPackageDialog,
  AddSpecialPriceDialog,
  SendPriceChange,
  SubmitPricingDialog,
} from "@/screens/Pricing/components/dialogs"
import PriceChangeFilterChips from "@/screens/Pricing/components/filterChips/PriceChangeFilterChips"
import PriceChangeFilter from "@/screens/Pricing/components/filters/PriceChangeFilter"
import { BULK_UPDATE_APPLYING_HIDE_DELAY_MS, PricingDialogs } from "@/screens/Pricing/constants"
import { useBulkUpdate } from "@/screens/Pricing/hooks/useBulkUpdate"
import { usePricingDialog } from "@/screens/Pricing/hooks/usePricingDialog"
import type { BulkUpdateFormValues } from "@/screens/Pricing/schema/BulkUpdateSchema"
import { usePriceChangeTablesStore, usePriceTableEditsStore } from "@/screens/Pricing/stores"
import { mapBulkUpdateFormValuesToInput } from "@/screens/Pricing/utils/priceChangeSubmit"
import { formatDate } from "@/utils/date"

const PriceChangeContainer: FC = () => {
  const searchParams = useSearch({
    from: "/app/pricing/_layout/price-change",
    strict: true,
  })

  const hasFilterParams = Boolean(Object.keys(searchParams).length)
  const isFetchingPriceChangeItems = usePriceChangeTablesStore((s) => s.isFetching)

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(true)
  const [isFilterChipsOpen, setIsFilterChipsOpen] = useState<boolean>(false)
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState<boolean>(false)
  const [isApplyingBulkUpdate, setIsApplyingBulkUpdate] = useState<boolean>(false)

  const navigate = useNavigate()
  const tablesRef = useRef<PriceChangeTablesHandle>(null)
  const [submitContext, setSubmitContext] = useState<PriceChangeSubmitContext | null>(null)
  const {
    appliedBulkUpdate,
    pendingBulkUpdate,
    handleBulkUpdateApply,
    handleBulkUpdateConfirm,
    handleBulkUpdateCancel,
    handleBulkUpdateClear,
    clearAppliedBulkUpdate,
  } = useBulkUpdate({ searchParams })
  const hasUnsavedEdits = usePriceTableEditsStore((s) => s.hasEdits)

  const bulkUpdate = useMemo(
    () => (appliedBulkUpdate ? mapBulkUpdateFormValuesToInput(appliedBulkUpdate) : undefined),
    [appliedBulkUpdate]
  )

  const isTrackedBulkUpdateApplyRef = useRef(false)

  const isFirstBulkUpdateRenderRef = useRef(true)
  useEffect(() => {
    if (isFirstBulkUpdateRenderRef.current) {
      isFirstBulkUpdateRenderRef.current = false
      return
    }

    tablesRef.current?.reseed()

    if (!isTrackedBulkUpdateApplyRef.current) return
    isTrackedBulkUpdateApplyRef.current = false

    const hideTimeoutId = setTimeout(() => setIsApplyingBulkUpdate(false), BULK_UPDATE_APPLYING_HIDE_DELAY_MS)

    return () => clearTimeout(hideTimeoutId)
  }, [bulkUpdate])

  const handleApplyBulkUpdate = (values: BulkUpdateFormValues) => {
    if (hasUnsavedEdits) {
      handleBulkUpdateApply(values)
      return
    }

    setIsApplyingBulkUpdate(true)
    isTrackedBulkUpdateApplyRef.current = true
    setTimeout(() => handleBulkUpdateApply(values), 0)
  }

  const handleConfirmBulkUpdate = () => {
    setIsApplyingBulkUpdate(true)
    isTrackedBulkUpdateApplyRef.current = true
    setTimeout(() => handleBulkUpdateConfirm(), 0)
  }

  const {
    isDialogOpen,
    setIsDialogOpen,
    setSelectedProduct,
    selectedProduct,
    selectedSubmissionId,
    setSelectedSubmissionId,
  } = usePricingDialog()
  const { hasPermissions } = usePermissionManager()

  const hasMaintenancePermission = hasPermissions([
    PermissionActionType.PRICING_MANAGER,
    PermissionActionType.PRICING_MAINTENANCE,
  ])

  const handleDialogClose = () => {
    setIsDialogOpen(null)
    setSelectedProduct(null)
    setSelectedSubmissionId(null)
  }

  const handleSubmitDialogOpen = () => {
    const context = tablesRef.current?.getSubmitContext()
    if (!context) {
      return
    }

    setSubmitContext(context)
    setIsDialogOpen(PricingDialogs.SubmitPriceChange)
  }

  const handlePricingSubmitSuccess = (submissionId: string) => {
    if (searchParams.changeType === PriceChangeType.SellingPrice) {
      setSelectedSubmissionId(submissionId)
      setIsDialogOpen(PricingDialogs.SendSellingPrice)
      return
    }

    handleDialogClose()
    tablesRef.current?.clearChanges()

    return navigate({
      to: "/app/pricing/history/purchase-cost",
      search: {
        order: SortingOrder.Desc,
        field: PriceChangeHistorySortField.UpdatedOn,
        changeType: PriceChangeType.PurchasingCost,
      },
    })
  }

  const handlePublishDraft = () => {
    tablesRef.current?.clearChanges()
    handleDialogClose()
    navigate({
      to: "/app/pricing/history/price-change",
      search: {
        order: SortingOrder.Desc,
        field: PriceChangeHistorySortField.UpdatedOn,
        changeType: PriceChangeType.SellingPrice,
      },
    })
  }

  return (
    <Box className='flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto'>
      {/*  Filter section */}
      <Paper className='flex shrink-0 flex-col border border-gray-400 bg-gray-100 p-6' elevation={0}>
        <Box className='flex items-start justify-between'>
          <Typography variant='h6' className='font-normal text-gray-800'>
            Filters
          </Typography>
          <IconButton
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            aria-expanded={isFilterOpen}
            aria-label='show more'
          >
            {isFilterOpen ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Box>

        <Collapse in={isFilterOpen}>
          <PriceChangeFilter hasAppliedBulkUpdate={Boolean(bulkUpdate)} />
        </Collapse>
      </Paper>

      {/*  Filter Chips */}
      <Fade in={hasFilterParams} unmountOnExit>
        <Paper className='flex shrink-0 flex-col border border-gray-400 bg-gray-100 p-6' elevation={0}>
          <Box className='flex items-start justify-between'>
            <Typography variant='h6' className='font-normal text-gray-800'>
              Applied Filters
            </Typography>
            <IconButton
              onClick={() => setIsFilterChipsOpen(!isFilterChipsOpen)}
              aria-expanded={isFilterChipsOpen}
              aria-label='show more'
            >
              {isFilterChipsOpen ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
            </IconButton>
          </Box>

          <Collapse in={isFilterChipsOpen}>
            <PriceChangeFilterChips />
          </Collapse>
        </Paper>
      </Fade>

      {/*  Bulk Update Options */}
      <Fade in={hasFilterParams && !isFetchingPriceChangeItems} unmountOnExit>
        <Paper className='flex shrink-0 flex-col border border-gray-400 bg-gray-100 p-6' elevation={0}>
          <Box className='flex items-start justify-between'>
            <Typography variant='h6' className='font-normal text-gray-800'>
              Bulk Update Options
            </Typography>
            <IconButton
              onClick={() => setIsBulkUpdateOpen(!isBulkUpdateOpen)}
              aria-expanded={isBulkUpdateOpen}
              aria-label='show more'
            >
              {isBulkUpdateOpen ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
            </IconButton>
          </Box>

          <Collapse in={isBulkUpdateOpen}>
            <BulkUpdateOptions
              onApply={handleApplyBulkUpdate}
              onClear={handleBulkUpdateClear}
              readonly={!hasMaintenancePermission}
              priceChangeType={searchParams.changeType}
              hasAppliedBulkUpdate={Boolean(bulkUpdate)}
            />
          </Collapse>
        </Paper>
      </Fade>

      <PriceChangeTables
        ref={tablesRef}
        setDialogOpen={setIsDialogOpen}
        openSubmitDialog={handleSubmitDialogOpen}
        setSelectedProduct={setSelectedProduct}
        readonly={!hasMaintenancePermission}
        bulkUpdate={bulkUpdate}
      />

      {isApplyingBulkUpdate && <BulkUpdateApplyingDialog />}

      {pendingBulkUpdate && (
        <ConfirmationDialog
          title='Apply bulk update?'
          content='Applying this bulk update will discard your existing manual changes.'
          closeLabel='Cancel'
          confirmLabel='Apply'
          isWarning
          onClose={handleBulkUpdateCancel}
          onConfirm={handleConfirmBulkUpdate}
        />
      )}

      {isDialogOpen === PricingDialogs.SubmitPriceChange && submitContext && (
        <SubmitPricingDialog
          submitContext={submitContext}
          bulkUpdate={bulkUpdate}
          onClose={handleDialogClose}
          onBeforeSubmit={clearAppliedBulkUpdate}
          onSuccess={handlePricingSubmitSuccess}
        />
      )}

      {isDialogOpen === PricingDialogs.SendSellingPrice && submitContext && selectedSubmissionId && (
        <SendPriceChange
          submitContext={submitContext}
          submissionId={selectedSubmissionId}
          onClose={handleDialogClose}
          onSuccess={handlePublishDraft}
        />
      )}

      {isDialogOpen === PricingDialogs.AddSpecialPrice && selectedProduct && (
        <AddSpecialPriceDialog
          onSuccess={() => {
            tablesRef.current?.refetch()
            handleDialogClose()
          }}
          changeType={searchParams.changeType}
          onClose={handleDialogClose}
          selectedProduct={selectedProduct}
        />
      )}

      {isDialogOpen === PricingDialogs.AddCostPackage && selectedProduct && (
        <AddCostPackageDialog
          onSuccess={() => {
            tablesRef.current?.refetch()
            handleDialogClose()
          }}
          onClose={handleDialogClose}
          selectedProduct={selectedProduct}
        />
      )}

      {isDialogOpen === PricingDialogs.DefaultedEffectiveDateConfirmation && (
        <ConfirmationDialog
          title='Effective Date defaulted'
          content={`Some rows have no Effective Date, so today's date (${formatDate(dayjs().toISOString())}) will be used. Do you want to continue?`}
          onClose={() => handleDialogClose()}
          confirmLabel='Continue'
          closeLabel='Cancel'
          onConfirm={handleSubmitDialogOpen}
        />
      )}

      {isDialogOpen === PricingDialogs.AutoResetEffectiveDateConfirmation && (
        <ConfirmationDialog
          title='Are you sure you want to update this price?'
          content='The price has an effective date in the past. Saving this change will update the effective date to today. You will not be able to undo this change.'
          onClose={() => handleDialogClose()}
          confirmLabel='Update Price'
          closeLabel='Cancel'
          onConfirm={() => {
            handleDialogClose()
            tablesRef.current?.resetInvalidEffectiveDates()
          }}
        />
      )}
    </Box>
  )
}

export default PriceChangeContainer
